import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @mui
import {
  Box,
  Select,
  MenuItem,
  Drawer,
  Typography,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled, useTheme, alpha } from '@mui/material/styles';
// redux
import { setActiveCampus } from '../../../redux/slices/meta';
// utils
import axios from '../../../utils/axios';
import { readStoredCampus } from '../../../utils/activeCampus';

// ----------------------------------------------------------------------
// Global campus switcher for members (role === 'user').
//
// A member has a home campus (their company's location) and an active campus
// (the location they are currently viewing/booking in). Switching updates the
// server-side active campus (CompanyUserSettings.active_campus, which every
// member API resolves against) plus the client-side activeCampus that axios
// injects, then hard-reloads so every campus-scoped screen refetches cleanly.
//
// Variants:
//   variant="select"  - full dropdown for the desktop header.
//   variant="chip"     - default compact pill that opens the bottom sheet.
//   children (fn)      - render-prop trigger, so each mobile dashboard template
//                        can make its OWN campus pill open the sheet and stay
//                        visually native. Receives { open, hasMultiple, switching }.
// On mobile the shared bottom sheet ("Explore our branches") is the picker.
// ----------------------------------------------------------------------

const CampusSelector = styled(Select)(({ theme }) => ({
  marginRight: theme.spacing(1),
  minWidth: 150,
  maxWidth: 180,
  height: 40,
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.primary,
  [theme.breakpoints.up('lg')]: {
    marginRight: theme.spacing(2),
    minWidth: 210,
    maxWidth: 'none',
  },
  '.MuiSelect-select': {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('lg')]: { paddingLeft: theme.spacing(2) },
  },
  '&.MuiOutlinedInput-root': {
    '& fieldset': { borderColor: theme.palette.grey[300] },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
  '& .MuiSvgIcon-root': { color: theme.palette.grey[600] },
}));

/** Shared data + switch behaviour for every variant. */
function useCampusSwitch() {
  const dispatch = useDispatch();
  const knownCampuses = useSelector((state) => state.meta.allCampuses);
  const [campuses, setCampuses] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [switching, setSwitching] = useState(null); // id being switched to

  const fetchCampuses = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/v1/accounts/user/my-campuses/');
      let list = data.campuses || [];
      const home = data.home_campus_id || null;
      const active = data.active_campus_id || home || (list[0] && list[0].id) || '';

      // Never leave a member without a way home. The server already keeps the
      // home campus in this list, but a member's door pass is issued for their
      // ACTIVE campus and a kiosk refuses one issued anywhere else, so a member
      // away from home with no way back cannot walk into the campus they pay
      // for. That is too sharp an edge to rest on one layer, so the client puts
      // home back if it ever comes back missing. Worst case the switch is
      // refused and they see why, which still beats an inert chip.
      if (home && active !== home && !list.some((c) => c.id === home)) {
        const known = (knownCampuses || []).find((c) => String(c.id) === String(home));
        list = [...list, { id: home, name: known?.name || 'Home campus',
                           city: known?.city, is_home: true }];
      }

      setCampuses(list);
      setActiveId(active);
    } catch (err) {
      console.error('Failed to load campuses for switcher:', err);
    }
  }, [knownCampuses]);

  useEffect(() => {
    fetchCampuses();
  }, [fetchCampuses]);

  const switchTo = useCallback(
    async (newId) => {
      if (!newId || switching) return;
      const chosen = campuses.find((c) => c.id === newId);
      if (!chosen) return;
      // Re-picking the campus already shown is allowed on purpose. This pill
      // reads the SERVER's active campus, while campus-scoped calls are made
      // against the client's stored one; if those ever disagree, tapping the
      // campus named here is what puts them back in step, and an early return
      // would make that tap do nothing.
      if (newId === activeId && String(readStoredCampus()?.id) === String(newId)) return;
      setSwitching(newId);
      try {
        await axios.post('/api/v1/accounts/user/select-campus/', { campus_id: newId });
        dispatch(setActiveCampus({ id: chosen.id, name: chosen.name, city: chosen.city }));
        // Hard reload lands the member on the chosen campus's dashboard, with
        // every campus-scoped screen refetched.
        window.location.reload();
      } catch (err) {
        console.error('Failed to switch campus:', err);
        setSwitching(null);
      }
    },
    [activeId, switching, campuses, dispatch]
  );

  return { campuses, activeId, switching, switchTo };
}

// ── Bottom sheet: "Explore our branches" ────────────────────────────────────
function CampusSwitchSheet({ open, onClose, campuses, activeId, switching, switchTo }) {
  const theme = useTheme();
  const active = campuses.find((c) => c.id === activeId);
  const others = campuses.filter((c) => c.id !== activeId);

  const renderRow = (campus, isCurrent) => (
    <Box
      key={campus.id}
      onClick={() => !isCurrent && switchTo(campus.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        cursor: isCurrent ? 'default' : 'pointer',
        border: '1px solid',
        borderColor: isCurrent ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider,
        bgcolor: isCurrent ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
        transition: 'background-color .15s, border-color .15s',
        '&:active': isCurrent ? {} : { bgcolor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.12),
        }}
      >
        <LocationOnIcon sx={{ fontSize: 22, color: theme.palette.primary.main }} />
      </Box>

      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
            {campus.name}
          </Typography>
          {campus.is_home && (
            <Chip
              label="Home"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap>
          {isCurrent ? "You're here" : campus.city || 'Tap to switch'}
        </Typography>
      </Box>

      {switching === campus.id ? (
        <CircularProgress size={20} sx={{ flexShrink: 0 }} />
      ) : isCurrent ? (
        <CheckCircleIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
      ) : (
        <ChevronRightIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
      )}
    </Box>
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          px: 2.5,
          pt: 1.25,
          pb: 'calc(20px + env(safe-area-inset-bottom))',
          maxHeight: '82vh',
        },
      }}
    >
      {/* Grabber */}
      <Box
        sx={{
          width: 40,
          height: 4,
          borderRadius: 2,
          bgcolor: 'divider',
          mx: 'auto',
          mb: 2,
        }}
      />

      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.25 }}>
        Explore our branches
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Switch your active location to browse and book there.
      </Typography>

      <Stack spacing={1.25}>
        {active && renderRow(active, true)}

        {others.length > 0 && (
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ pt: 1, px: 0.5, letterSpacing: 1 }}
          >
            Other branches
          </Typography>
        )}
        {others.map((c) => renderRow(c, false))}
      </Stack>
    </Drawer>
  );
}

// ── Default compact pill trigger (used when no render-prop is supplied) ──────
function DefaultChip({ active, hasMultiple, switching, onClick }) {
  const theme = useTheme();
  return (
    <Chip
      size="small"
      clickable={hasMultiple}
      onClick={hasMultiple ? onClick : undefined}
      icon={
        switching ? (
          <CircularProgress size={13} sx={{ ml: 0.5 }} />
        ) : (
          <LocationOnIcon sx={{ fontSize: 16 }} />
        )
      }
      {...(hasMultiple
        ? { deleteIcon: <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />, onDelete: onClick }
        : {})}
      label={active ? active.name : 'Workspace'}
      sx={{
        bgcolor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.main,
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-icon': { color: theme.palette.primary.main },
        '& .MuiChip-deleteIcon': { color: theme.palette.primary.main },
      }}
    />
  );
}

// ── Trigger + sheet wrapper (mobile / render-prop) ──────────────────────────
function TriggerSwitcher({ variant, children }) {
  const sw = useCampusSwitch();
  const [open, setOpen] = useState(false);
  const hasMultiple = sw.campuses.length > 1;
  const active = sw.campuses.find((c) => c.id === sw.activeId);
  const openSheet = () => {
    if (hasMultiple) setOpen(true);
  };

  const trigger =
    typeof children === 'function' ? (
      children({ open: openSheet, hasMultiple, switching: Boolean(sw.switching) })
    ) : (
      <DefaultChip
        active={active}
        hasMultiple={hasMultiple}
        switching={Boolean(sw.switching)}
        onClick={openSheet}
      />
    );

  return (
    <>
      {trigger}
      <CampusSwitchSheet open={open} onClose={() => setOpen(false)} {...sw} />
    </>
  );
}

// ── Full select variant (desktop header) ────────────────────────────────────
function SelectSwitcher() {
  const theme = useTheme();
  const { campuses, activeId, switching, switchTo } = useCampusSwitch();

  if (campuses.length <= 1) return null;

  return (
    <CampusSelector
      value={activeId}
      onChange={(e) => switchTo(e.target.value)}
      displayEmpty
      disabled={Boolean(switching)}
      MenuProps={{ PaperProps: { sx: { maxHeight: 340, maxWidth: '85vw', mt: 1 } } }}
      renderValue={(selected) => {
        const c = campuses.find((x) => x.id === selected);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {switching ? (
              <CircularProgress size={16} sx={{ mr: 1, flexShrink: 0 }} />
            ) : (
              <LocationOnIcon
                sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main, flexShrink: 0 }}
              />
            )}
            <Typography color="text.primary" noWrap>
              {c ? c.name : 'Select a Campus'}
            </Typography>
          </Box>
        );
      }}
    >
      {campuses.map((campus) => (
        <MenuItem
          key={campus.id}
          value={campus.id}
          sx={{ '&:hover': { backgroundColor: theme.palette.grey[50] } }}
        >
          <LocationOnIcon
            sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main, flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography color="text.primary" noWrap>
              {campus.name}
            </Typography>
            {campus.city && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {campus.city}
              </Typography>
            )}
          </Box>
          {campus.is_home && (
            <Chip
              label="Home"
              size="small"
              sx={{ ml: 1, height: 20, fontSize: 11, flexShrink: 0 }}
              color="primary"
              variant="outlined"
            />
          )}
        </MenuItem>
      ))}
    </CampusSelector>
  );
}

export default function MemberCampusSwitcher({ variant = 'select', children }) {
  // A render-prop trigger (or the chip variant) always uses the bottom sheet.
  // Only the explicit desktop-header select falls through to the dropdown.
  if (typeof children === 'function' || variant === 'chip') {
    return <TriggerSwitcher variant={variant}>{children}</TriggerSwitcher>;
  }
  return <SelectSwitcher />;
}
